import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, API } from "../lib/api";
import Footer from "../components/Footer";
import { SiteLogo } from "../components/SiteLogo";
import { ArrowLeft } from "lucide-react";
import { LangSwitcher } from "../components/LangSwitcher";
import { t } from "../i18n";

const WIDTHS = { small: "40%", medium: "70%", full: "100%" };

function Block({ b }) {
  if (b.type === "texto") return <div className="lp-text" dangerouslySetInnerHTML={{ __html: b.html || "" }} />;
  if (b.type === "imagem" && b.url) {
    const src = b.url.startsWith("/api/") ? `${API}${b.url.replace(/^\/api/, "")}` : b.url;
    const margin = b.alinhamento === "left" ? "0 auto 0 0" : b.alinhamento === "right" ? "0 0 0 auto" : "0 auto";
    return (
      <figure className="my-6" style={{ textAlign: b.alinhamento || "center" }}>
        <img src={src} alt={b.alt || ""} loading="lazy" style={{ width: WIDTHS[b.largura] || "100%", maxWidth: "100%", height: "auto", margin, borderRadius: 8 }} />
        {b.legenda && <figcaption className="text-sm text-muted-foreground mt-1.5">{b.legenda}</figcaption>}
      </figure>
    );
  }
  if (b.type === "botao") {
    const cls = b.estilo === "primario"
      ? "bg-primary text-primary-foreground"
      : "bg-transparent text-foreground border border-border";
    return (
      <div className="my-6">
        <a href={b.url} target={b.novaAba ? "_blank" : undefined} rel={b.novaAba ? "noopener noreferrer" : undefined}
          className={`inline-block px-6 py-3 rounded-md font-bold hover:opacity-90 transition-opacity ${cls}`}>{b.texto || t("Botão")}</a>
      </div>
    );
  }
  if (b.type === "divisor") return <hr className="my-9 border-border" />;
  return null;
}

export default function PublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    setState("loading");
    api.get(`/pages/public/${slug}`)
      .then(({ data }) => {
        if (!alive) return;
        if (data.redirect) { navigate(`/${data.redirect}`, { replace: true }); return; }
        setPage(data.page); setState("ok");
      })
      .catch(() => { if (alive) setState("404"); });
    return () => { alive = false; };
  }, [slug, navigate]);

  if (state === "loading") return <div className="min-h-screen bg-background flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  if (state === "404") return <div className="min-h-screen bg-background flex flex-col items-center justify-center text-center px-6"><h1 className="text-4xl font-extrabold mb-2">404</h1><p className="text-muted-foreground">{t("Página não encontrada.")}</p></div>;

  const seo = page.seo || {};
  const firstImg = (page.blocos || []).find((b) => b.type === "imagem" && b.url)?.url || "";
  const title = seo.meta_title || page.titulo;
  const desc = seo.meta_description || "";
  const ogImg = seo.og_image || firstImg;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = seo.canonical_url || `${origin}/${page.slug}`;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="public-page">
      <Helmet>
        <title>{title}</title>
        {desc && <meta name="description" content={desc} />}
        <meta name="robots" content={seo.indexar_pagina === false ? "noindex,nofollow" : "index,follow"} />
        <meta property="og:title" content={seo.og_title || title} />
        {(seo.og_description || desc) && <meta property="og:description" content={seo.og_description || desc} />}
        {ogImg && <meta property="og:image" content={ogImg.startsWith("/api/") ? `${API}${ogImg.replace(/^\/api/, "")}` : ogImg} />}
        <meta property="og:type" content="article" />
        {canonical && <link rel="canonical" href={canonical} />}
        {canonical && <meta property="og:url" content={canonical} />}
      </Helmet>
      <header className="border-b border-border/60 bg-card/60" data-testid="page-header">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="page-home-logo"><SiteLogo className="h-7 w-auto" /></a>
          <div className="flex items-center gap-3"><LangSwitcher /><a href="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="page-home-btn"><ArrowLeft className="h-4 w-4" /> {t("Voltar ao início")}</a></div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-14 pb-24">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-6" data-testid="public-page-title">{page.titulo}</h1>
        <article className="lp-content leading-relaxed">
          {(page.blocos || []).map((b, i) => <Block key={i} b={b} />)}
        </article>
      </main>
      <Footer />
    </div>
  );
}
